import { OVERLAY_OPEN, OVERLAY_CLOSE } from './arctic-header-events.js';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class ArcticMobileMenu extends HTMLElement {
  constructor() {
    super();
    this.currentPanel = 'root';
    this.closeTimer = null;
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-mobile-menu-trigger]');
    this.drawer = this.querySelector('[data-mobile-menu-drawer]');
    this.closeButtons = Array.from(this.querySelectorAll('[data-mobile-menu-close]'));
    this.openButtons = Array.from(this.querySelectorAll('[data-mobile-menu-open]'));
    this.backButtons = Array.from(this.querySelectorAll('[data-mobile-menu-back]'));
    this.searchButtons = Array.from(this.querySelectorAll('[data-mobile-menu-search]'));
    this.panels = Array.from(this.querySelectorAll('[data-mobile-menu-panel]'));
    this.rootPanel = this.querySelector('[data-mobile-menu-panel="root"]');

    this.trigger?.addEventListener('click', this.onTriggerClick);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.onCloseClick));
    this.openButtons.forEach((button) => button.addEventListener('click', this.onPanelOpenClick));
    this.backButtons.forEach((button) => button.addEventListener('click', this.onBackClick));
    this.searchButtons.forEach((button) => button.addEventListener('click', this.onSearchClick));
    this.drawer?.addEventListener('keydown', this.onDrawerKeydown);
    document.addEventListener('keydown', this.onDocumentKeydown);
    this.showRoot({ focus: false });
  }

  disconnectedCallback() {
    clearTimeout(this.closeTimer);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  open({ focus = true } = {}) {
    if (!this.drawer || this.classList.contains('is-open')) return;

    clearTimeout(this.closeTimer);
    this.drawer.hidden = false;
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.showRoot({ focus: false });

    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.drawer.classList.add('is-open');
      if (focus) this.getFocusableElements()[0]?.focus({ preventScroll: true });
    });

    this.dispatchEvent(
      new CustomEvent(OVERLAY_OPEN, {
        bubbles: true,
        detail: { el: this.drawer, source: 'mobile-menu' },
      })
    );
  }

  close({ restoreFocus = false } = {}) {
    if (!this.drawer || !this.classList.contains('is-open')) return;

    this.classList.remove('is-open');
    this.drawer.classList.remove('is-open');
    this.trigger?.setAttribute('aria-expanded', 'false');

    this.dispatchEvent(
      new CustomEvent(OVERLAY_CLOSE, {
        bubbles: true,
        detail: { el: this.drawer, source: 'mobile-menu' },
      })
    );

    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.drawer.hidden = true;
      this.showRoot({ focus: false });
    }, 220);

    if (restoreFocus) this.trigger?.focus({ preventScroll: true });
  }

  showPanel(id, { focus = true } = {}) {
    const target = this.querySelector(`[data-mobile-menu-panel="${CSS.escape(id)}"]`);
    if (!target || target === this.rootPanel) return;

    this.panels.forEach((panel) => {
      if (panel === this.rootPanel) {
        this.setPanelState(panel, { hidden: false, active: false, parent: true });
        return;
      }

      const isTarget = panel === target;
      this.setPanelState(panel, { hidden: !isTarget, active: isTarget, parent: false });
    });

    this.currentPanel = id;
    this.setOpenButtonState(id);
    if (focus) target.querySelector(FOCUSABLE_SELECTOR)?.focus({ preventScroll: true });
  }

  showRoot({ focus = true } = {}) {
    this.panels.forEach((panel) => {
      const isRoot = panel === this.rootPanel;
      this.setPanelState(panel, { hidden: !isRoot, active: isRoot, parent: false });
    });

    this.currentPanel = 'root';
    this.setOpenButtonState(null);
    if (focus) this.rootPanel?.querySelector(FOCUSABLE_SELECTOR)?.focus({ preventScroll: true });
  }

  getFocusableElements() {
    if (!this.drawer) return [];
    return Array.from(this.drawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
      if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
      return element.offsetParent !== null || element === document.activeElement;
    });
  }

  setPanelState(panel, { hidden, active, parent }) {
    panel.hidden = hidden;
    panel.classList.toggle('is-active', active);
    panel.classList.toggle('is-parent', parent);
    panel.setAttribute('aria-hidden', active ? 'false' : 'true');
    panel.toggleAttribute('inert', !active);
  }

  setOpenButtonState(activeId) {
    this.openButtons.forEach((button) => {
      button.setAttribute('aria-expanded', button.dataset.mobileMenuOpen === activeId ? 'true' : 'false');
    });
  }

  onTriggerClick = (event) => {
    this.open({ focus: event.detail === 0 });
  };

  onCloseClick = (event) => {
    this.close({ restoreFocus: event.detail === 0 });
  };

  onPanelOpenClick = (event) => {
    const id = event.currentTarget?.dataset.mobileMenuOpen;
    if (id) this.showPanel(id, { focus: event.detail === 0 });
  };

  onBackClick = (event) => {
    this.showRoot({ focus: event.detail === 0 });
  };

  onSearchClick = (event) => {
    event.preventDefault();
    this.close({ restoreFocus: false });

    window.setTimeout(() => {
      const search = document.querySelector('arctic-header-search[data-mode="mobile"]');
      if (!search) return;

      search.open?.();
      search.querySelector('[data-header-search-input]')?.focus({ preventScroll: true });
    }, 240);
  };

  onDrawerKeydown = (event) => {
    if (event.key !== 'Tab') return;

    const focusable = this.getFocusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  };

  onDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !this.classList.contains('is-open')) return;

    event.preventDefault();
    if (this.currentPanel !== 'root') {
      this.showRoot();
      return;
    }

    this.close({ restoreFocus: true });
  };
}

if (!customElements.get('arctic-mobile-menu')) {
  customElements.define('arctic-mobile-menu', ArcticMobileMenu);
}
